import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  KeyboardArrowUp as ArrowUpIcon,
  KeyboardArrowDown as ArrowDownIcon,
  Remove as RemoveIcon,
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

interface Player {
  id: string;
}

interface SubmittedRankingViewProps {
  player: Player;
  sport: string;
}

interface RankingReportSummary {
  reportId: string;
  categoryId: string;
  sport: string;
  periodKey: string;
  periodEndDate: string;
  submittedAt: string;
  score: number;
  grade: string;
  percentage: string;
  totalStrokes?: number;
}

interface StrokeWithMeta {
  code: string;
  rating: number | null;
  courtPosition?: string;
  halfSide?: string;
  strokeType?: string;
  strokeAction?: string;
  shotType?: string;
  placement?: string;
  displayLabel: string;
}

interface RankingReportDetail {
  reportId: string;
  categoryId: string;
  sport: string;
  periodKey: string;
  periodEndDate: string;
  submittedAt?: string;
  strokes: StrokeWithMeta[];
}

function strokeDisplayLabel(s: {
  courtPosition?: string;
  halfSide?: string;
  strokeType?: string;
  strokeAction?: string;
  shotType?: string;
  placement?: string;
  code?: string;
}): string {
  const parts = [
    s.courtPosition,
    s.halfSide,
    s.strokeType,
    s.strokeAction,
    s.shotType,
    s.placement,
  ].filter((v) => v != null && String(v).trim() !== '');
  return parts.length > 0 ? parts.join(' · ') : (s.code || '');
}

const MAX_COMPARE = 3;
const CATEGORY_LABELS: Record<string, string> = {
  'self-evaluation': 'Self Evaluation',
  'coach-academy': 'Coach/Academy Evaluation',
  'practical-test': 'Board Exam',
};

const SubmittedRankingView: React.FC<SubmittedRankingViewProps> = ({ player, sport }) => {
  const [reports, setReports] = useState<RankingReportSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedForCompare, setSelectedForCompare] = useState<string[]>([]);
  const [detailCache, setDetailCache] = useState<Record<string, RankingReportDetail>>({});
  const [loadingDetails, setLoadingDetails] = useState<Record<string, boolean>>({});
  const [onlyStrokesWithDelta, setOnlyStrokesWithDelta] = useState(false);

  useEffect(() => {
    loadReports();
  }, [player.id, sport]);

  const loadReports = async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      qs.set('sport', sport);
      const response = await fetch(`/api/ranking-reports/player/${player.id}?${qs.toString()}`);
      const result = await response.json();
      if (result.success && Array.isArray(result.data)) {
        setReports(result.data);
      } else {
        setReports([]);
      }
    } catch (error) {
      console.error('Error loading ranking reports:', error);
      setReports([]);
    } finally {
      setLoading(false);
    }
  };

  const loadReportDetail = async (reportId: string) => {
    if (detailCache[reportId]) return;
    setLoadingDetails((prev) => ({ ...prev, [reportId]: true }));
    try {
      const response = await fetch(`/api/ranking-reports/${encodeURIComponent(reportId)}`);
      const result = await response.json();
      if (result.success && result.data) {
        const strokes = (result.data.strokes || []).map((s: any) => ({
          code: s.code || '',
          rating: s.rating != null ? Number(s.rating) : null,
          courtPosition: s.courtPosition,
          halfSide: s.halfSide,
          strokeType: s.strokeType,
          strokeAction: s.strokeAction,
          shotType: s.shotType,
          placement: s.placement,
          displayLabel: strokeDisplayLabel({
            courtPosition: s.courtPosition,
            halfSide: s.halfSide,
            strokeType: s.strokeType,
            strokeAction: s.strokeAction,
            shotType: s.shotType,
            placement: s.placement,
            code: s.code,
          }),
        }));
        setDetailCache((prev) => ({
          ...prev,
          [reportId]: {
            reportId: result.data.reportId,
            categoryId: result.data.categoryId,
            sport: result.data.sport,
            periodKey: result.data.periodKey,
            periodEndDate: result.data.periodEndDate,
            submittedAt: result.data.updatedAt,
            strokes,
          },
        }));
      }
    } catch (error) {
      console.error('Error loading report detail:', error);
    } finally {
      setLoadingDetails((prev) => ({ ...prev, [reportId]: false }));
    }
  };

  useEffect(() => {
    selectedForCompare.forEach((id) => loadReportDetail(id));
  }, [selectedForCompare]);

  const toggleCompare = (reportId: string) => {
    setSelectedForCompare((prev) =>
      prev.includes(reportId)
        ? prev.filter((id) => id !== reportId)
        : prev.length >= MAX_COMPARE
          ? prev
          : [...prev, reportId]
    );
  };

  // Order by evaluation period: earliest = Report 1, latest = Report 2 (so trend = latest vs earlier)
  const selectedReports = useMemo(() => {
    const filtered = reports.filter((r) => selectedForCompare.includes(r.reportId));
    return [...filtered].sort((a, b) => {
      const dateA = new Date(a.submittedAt || a.periodEndDate || 0).getTime();
      const dateB = new Date(b.submittedAt || b.periodEndDate || 0).getTime();
      return dateA - dateB;
    });
  }, [reports, selectedForCompare]);

  const comparisonData = useMemo(() => {
    if (selectedReports.length < 2) return { chartData: [], tableRows: [] };
    const details = selectedReports
      .map((r) => detailCache[r.reportId])
      .filter(Boolean) as RankingReportDetail[];
    if (details.length !== selectedReports.length) return { chartData: [], tableRows: [] };

    const byCode: Record<string, { code: string; displayLabel: string; ratings: (number | null)[] }> = {};
    details.forEach((d, idx) => {
      d.strokes.forEach((s) => {
        if (!byCode[s.code]) {
          byCode[s.code] = { code: s.code, displayLabel: s.displayLabel || s.code, ratings: [] };
        }
        if (s.displayLabel && s.displayLabel.trim()) {
          byCode[s.code].displayLabel = s.displayLabel;
        }
        while (byCode[s.code].ratings.length < details.length) byCode[s.code].ratings.push(null);
        byCode[s.code].ratings[idx] = s.rating;
      });
    });
    Object.values(byCode).forEach((r) => {
      while (r.ratings.length < details.length) r.ratings.push(null);
    });
    const tableRows = Object.values(byCode).sort((a, b) => a.code.localeCompare(b.code));
    const chartData = tableRows.map((row, i) => {
      const point: Record<string, string | number> = {
        index: i + 1,
        fullLabel: row.displayLabel || row.code,
      };
      details.forEach((_, idx) => {
        const v = row.ratings[idx];
        point[`r${idx}`] = v != null ? v : 0;
      });
      return point;
    });
    return { chartData, tableRows };
  }, [selectedReports, detailCache]);

  const hasDelta = (ratings: (number | null)[]) => {
    const values = ratings.filter((r) => r != null) as number[];
    return new Set(values).size > 1;
  };

  const { chartData: displayChartData, tableRows: displayTableRows } = useMemo(() => {
    if (!onlyStrokesWithDelta) {
      return { chartData: comparisonData.chartData, tableRows: comparisonData.tableRows };
    }
    const filtered = comparisonData.tableRows.filter((row) => hasDelta(row.ratings));
    const chartData = filtered.map((row, i) => {
      const point: Record<string, string | number> = {
        index: i + 1,
        fullLabel: row.displayLabel || row.code,
      };
      selectedReports.forEach((_, idx) => {
        const v = row.ratings[idx];
        point[`r${idx}`] = v != null ? v : 0;
      });
      return point;
    });
    return { chartData, tableRows: filtered };
  }, [comparisonData, onlyStrokesWithDelta, selectedReports]);

  const formatPeriodKey = (key: string) => {
    if (!key) return '';
    if (key.startsWith('Y-')) return `Yearly ${key.slice(2)}`;
    if (key.startsWith('Q')) return key;
    return key;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 320 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ pt: 1 }}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
        Submitted Ranking – {sport}
      </Typography>
      {reports.length === 0 ? (
        <Alert severity="info">No submitted ranking reports for {sport}.</Alert>
      ) : (
        <>
          <TableContainer component={Paper} sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell padding="checkbox"><strong>Compare</strong></TableCell>
                  <TableCell><strong>Period</strong></TableCell>
                  <TableCell><strong>Category</strong></TableCell>
                  <TableCell><strong>Submitted</strong></TableCell>
                  <TableCell align="right"><strong>Score</strong></TableCell>
                  <TableCell><strong>Grade</strong></TableCell>
                  <TableCell><strong>%</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reports.map((r) => (
                  <TableRow key={r.reportId} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedForCompare.includes(r.reportId)}
                        onChange={() => toggleCompare(r.reportId)}
                        disabled={selectedForCompare.length >= MAX_COMPARE && !selectedForCompare.includes(r.reportId)}
                      />
                    </TableCell>
                    <TableCell>{formatPeriodKey(r.periodKey)}</TableCell>
                    <TableCell>{CATEGORY_LABELS[r.categoryId] || r.categoryId}</TableCell>
                    <TableCell>
                      {r.submittedAt ? new Date(r.submittedAt).toLocaleDateString() : '–'}
                    </TableCell>
                    <TableCell align="right">{r.score}</TableCell>
                    <TableCell>
                      <Chip label={r.grade} size="small" color={r.grade >= 'A' ? 'success' : r.grade >= 'B' ? 'primary' : 'default'} />
                    </TableCell>
                    <TableCell>{r.percentage}%</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {selectedReports.length >= 2 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Compare reports (max {MAX_COMPARE})
              </Typography>
              {selectedReports.some((r) => loadingDetails[r.reportId]) ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2">Loading report details…</Typography>
                </Box>
              ) : null}
              {comparisonData.chartData.length > 0 && (
                <Paper sx={{ p: 2, mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Rating by stroke (hover on points for stroke details)</Typography>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={onlyStrokesWithDelta}
                          onChange={(e) => setOnlyStrokesWithDelta(e.target.checked)}
                          size="small"
                        />
                      }
                      label={<Typography variant="body2">Show only strokes with change</Typography>}
                    />
                  </Box>
                  <Box sx={{ bgcolor: '#f5f9fc', borderRadius: 1, p: 1 }}>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={displayChartData} margin={{ top: 10, right: 24, left: 8, bottom: 32 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#b0bec5" />
                        <XAxis
                          dataKey="index"
                          type="number"
                          domain={[1, Math.max(1, displayChartData.length)]}
                          tick={false}
                          axisLine={{ stroke: '#263238', strokeWidth: 2 }}
                          tickLine={false}
                          label={{ value: 'Strokes', position: 'bottom', offset: 8, style: { fontSize: 12, fontWeight: 600, fill: '#263238' } }}
                        />
                        <YAxis
                          domain={[1, 5]}
                          allowDecimals={false}
                          ticks={[1, 2, 3, 4, 5]}
                          tick={{ fontSize: 12, fill: '#263238', fontWeight: 600 }}
                          axisLine={{ stroke: '#263238', strokeWidth: 2 }}
                          tickLine={{ stroke: '#263238', strokeWidth: 1.5 }}
                          label={{ value: 'Rating', angle: -90, position: 'insideLeft', style: { fontSize: 12, fontWeight: 600, fill: '#263238', textAnchor: 'middle' } }}
                        />
                      <Tooltip
                        content={({ active, payload }) => {
                          if (!active || !payload?.length) return null;
                          const data = payload[0]?.payload;
                          const strokeLabel = data?.fullLabel ?? '';
                          return (
                            <Paper sx={{ p: 1.5, maxWidth: 360 }} elevation={3}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>Stroke</Typography>
                              <Typography variant="body2" sx={{ mb: 1, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{strokeLabel}</Typography>
                              {payload.map((p) => (
                                <Typography key={p.dataKey} variant="body2" sx={{ color: p.color, fontWeight: 500 }}>{p.name}: {p.value}</Typography>
                              ))}
                            </Paper>
                          );
                        }}
                      />
                      <Legend wrapperStyle={{ paddingTop: 8 }} formatter={(value) => <span style={{ fontWeight: 600, color: '#333' }}>{value}</span>} />
                      {selectedReports.map((r, idx) => (
                        <Line
                          key={r.reportId}
                          type="monotone"
                          dataKey={`r${idx}`}
                          name={`${formatPeriodKey(r.periodKey)} – ${CATEGORY_LABELS[r.categoryId] || r.categoryId}`}
                          stroke={['#1565c0', '#2e7d32', '#c62828'][idx] ?? '#ed6c02'}
                          strokeWidth={2.5}
                          dot={{ r: 5, fill: ['#1565c0', '#2e7d32', '#c62828'][idx], strokeWidth: 2, stroke: '#fff' }}
                          activeDot={{ r: 7, fill: ['#1565c0', '#2e7d32', '#c62828'][idx], strokeWidth: 2, stroke: '#fff' }}
                        />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>
                  {onlyStrokesWithDelta && displayChartData.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>No strokes with change between reports.</Typography>
                  )}
                </Paper>
              )}
              {comparisonData.tableRows.length > 0 && (
                <Paper sx={{ overflow: 'auto' }}>
                  {onlyStrokesWithDelta && (
                    <Box sx={{ px: 2, py: 1, bgcolor: '#f5f5f5', borderBottom: 1, borderColor: 'divider' }}>
                      <Typography variant="body2" color="text.secondary">
                        Showing only strokes with change ({displayTableRows.length} of {comparisonData.tableRows.length}).
                      </Typography>
                    </Box>
                  )}
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                        <TableCell sx={{ maxWidth: 360 }}><strong>Stroke</strong></TableCell>
                        {selectedReports.map((r, idx) => (
                          <TableCell key={r.reportId} align="center"><strong>Report {idx + 1}</strong></TableCell>
                        ))}
                        {selectedReports.length === 2 && <TableCell align="center"><strong>Trend</strong></TableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {displayTableRows.map((row, i) => (
                        <TableRow key={i}>
                          <TableCell sx={{ maxWidth: 360 }} title={row.displayLabel}>{row.displayLabel || row.code}</TableCell>
                          {row.ratings.map((val, idx) => (
                            <TableCell key={idx} align="center">
                              {val != null ? val : 'N/A'}
                            </TableCell>
                          ))}
                          {selectedReports.length === 2 && (
                            <TableCell align="center">
                              {row.ratings[0] != null && row.ratings[1] != null ? (
                                row.ratings[1] > row.ratings[0] ? <ArrowUpIcon color="success" fontSize="small" />
                                : row.ratings[1] < row.ratings[0] ? <ArrowDownIcon color="error" fontSize="small" />
                                : <RemoveIcon color="action" fontSize="small" />
                              ) : '–'}
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Paper>
              )}
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default SubmittedRankingView;
